import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import Tooltip from "@mui/material/Tooltip";
import DeleteIconButton from "_shared/_views/buttons/delete-icon-button";
import type { TeamRosterRow } from "_shared/entity-members/_views/team-roster-table";
import Guid from "_shared/guids/guid";
import { WorkTeamSide } from "work/work-agreements/teams/leader-handover/team-leader-handover";

/**
 * What a roster needs to offer the client team leader a member's removal from the work (#6070): the
 * team it is, whether a removal is already on its way, and the act itself.
 */
export type RemoveFromWork = {
  side: WorkTeamSide;
  busy: boolean;
  remove: (userId: Guid, displayName: string) => void;
};

export const TEAM_NAMES: Record<WorkTeamSide, string> = {
  [WorkTeamSide.Client]: "client team",
  [WorkTeamSide.Vendor]: "vendor team",
};

/**
 * One member row's **Remove from work** control, drawn as a button on a wide pane and as the row's one
 * menu action on a phone. The accessible name says whose row it is on: a column of buttons all called
 * "Remove" tells a screen reader nothing about who is about to lose the work.
 */
export function removeFromWorkRowActions(
  removal: RemoveFromWork,
  userId: Guid,
  displayName: string,
): Pick<TeamRosterRow, "actions" | "menuActions"> {
  const label = `Remove ${displayName} from the work`;
  const remove = () => removal.remove(userId, displayName);
  return {
    actions: (
      <Tooltip title={label}>
        <span>
          <DeleteIconButton
            aria-label={label}
            disabled={removal.busy}
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              remove();
            }}
          >
            <DeleteOutlinedIcon />
          </DeleteIconButton>
        </span>
      </Tooltip>
    ),
    menuActions: [
      {
        key: "remove-from-work",
        label: "Remove from work",
        ariaLabel: label,
        icon: <DeleteOutlinedIcon />,
        destructive: true,
        disabled: removal.busy,
        onClick: remove,
      },
    ],
  };
}
